import PendoScrollIndicator from '../scroll-indicator/pendo-scroll-indicator.js';
import PendoButton from '../button/pendo-button.vue.js';
import PendoTabPanel from './pendo-tab-panel.vue.js';
import { keyCodes } from '../../utils/utils.js';
import { getWidth } from '../../utils/dom.js';

const TAB_THEME_COLOR_MAP = {
  designer: '#128297',
  app: '#2A2C35'
};
const PREV_NEXT_CONTAINER_WIDTH = 40;
var script = {
  name: 'PendoTabs',
  components: {
    PendoScrollIndicator,
    PendoButton,
    PendoTabPanel
  },
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    value: {
      type: String,
      default: undefined,
      required: true
    },
    tabs: {
      type: Array,
      default: () => []
    },
    theme: {
      type: String,
      default: 'app',
      validator: theme => ['app', 'designer'].includes(theme)
    },
    card: {
      type: Boolean,
      default: false
    },
    vertical: {
      type: Boolean,
      default: false
    },
    scrollable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      focusOnNextModelUpdate: false
    };
  },
  watch: {
    value: 'onValueChange'
  },
  created() {
    if (!this.value) {
      this.activateTab(this.tabs[0], true);
    }
  },
  mounted() {
    this.setActiveTabColor();
  },
  methods: {
    onValueChange() {
      const newActiveTabIndex = this.tabs.findIndex(tab => tab.prop === this.value);
      const anyTabIsFocused = this.$refs.tab.some(tab => tab === document.activeElement);
      const shouldFocusNewTab = anyTabIsFocused && document.activeElement !== this.$refs.tab[newActiveTabIndex];
      if (this.focusOnNextModelUpdate || shouldFocusNewTab) {
        this.$refs.tab[newActiveTabIndex].focus();
        this.focusOnNextModelUpdate = false;
      }
    },
    onTabClick(tab) {
      if (tab.disabled) {
        return;
      }
      this.activateTab(tab, false);
      this.$emit('tab-click', tab);
    },
    onTabKeydown(event, tab) {
      const key = event.keyCode;
      switch (key) {
        case keyCodes.end:
          {
            event.preventDefault();
            const lastTab = this.tabs[this.tabs.length - 1];
            if (lastTab.lazy) {
              this.focusLastTab();
            } else {
              this.activateTab(lastTab, true);
            }
            break;
          }
        case keyCodes.home:
          {
            event.preventDefault();
            const firstTab = this.tabs[0];
            if (firstTab.lazy) {
              this.focusFirstTab();
            } else {
              this.activateTab(firstTab, true);
            }
            break;
          }
        case keyCodes.left:
        case keyCodes.right:
          if (!this.vertical) {
            event.preventDefault();
          }
          break;
        case keyCodes.up:
        case keyCodes.down:
          if (this.vertical) {
            event.preventDefault();
            this.switchTabOnArrowPress(event, tab);
          }
          break;
      }
    },
    onTabKeyup(event, tab) {
      switch (event.keyCode) {
        case keyCodes.left:
        case keyCodes.right:
          if (!this.vertical) {
            event.preventDefault();
            this.switchTabOnArrowPress(event, tab);
          }
          break;
        case keyCodes.enter:
        case keyCodes.space:
          this.activateTab(tab, true);
          break;
      }
    },
    onTabFocus(event, tab) {
      if (tab.disabled) {
        return;
      }
      if (event.target === document.activeElement) {
        if (tab.lazy) {
          return;
        }
        this.activateTab(tab, false);
      }
    },
    switchTabOnArrowPress(event, tab) {
      const navigationKeyCodes = {
        37: -1,
        38: -1,
        39: 1,
        40: 1
      };
      if (!navigationKeyCodes[event.keyCode]) {
        return;
      }
      const {
        prop
      } = tab;
      const index = this.tabs.findIndex(tab => tab.prop === prop);
      let nextIndex = parseInt(index, 10) + navigationKeyCodes[event.keyCode];
      while (this.tabs[nextIndex] && this.tabs[nextIndex].disabled) {
        nextIndex += navigationKeyCodes[event.keyCode];
      }
      if (this.$refs.tab[nextIndex]) {
        this.$refs.tab[nextIndex].focus();
        return;
      }
      switch (event.keyCode) {
        case keyCodes.left:
        case keyCodes.up:
          this.focusLastTab();
          break;
        case keyCodes.right:
        case keyCodes.down:
          this.focusFirstTab();
      }
    },
    focusFirstTab() {
      this.$refs.tab[0].focus();
    },
    focusLastTab() {
      this.$refs.tab[this.tabs.length - 1].focus();
    },
    activateTab(tab, setFocus) {
      if (tab.prop !== this.value) {
        this.$emit('change', tab.prop);
        this.focusOnNextModelUpdate = setFocus;
      }
    },
    setActiveTabColor() {
      this.$el.style.setProperty('--tab--active-color', TAB_THEME_COLOR_MAP[this.theme]);
    },
    onPrevButtonClick() {
      const {
        content
      } = this.$refs.content.$refs;
      const buttonWidth = PREV_NEXT_CONTAINER_WIDTH * 2;
      const width = getWidth(content) - buttonWidth;
      const pos = content.scrollLeft - width;
      content.scrollLeft = pos <= PREV_NEXT_CONTAINER_WIDTH ? 0 : pos;
    },
    onNextButtonClick() {
      const {
        content
      } = this.$refs.content.$refs;
      const contentWidth = getWidth(content);
      const distanceToEnd = content.scrollWidth - content.scrollLeft - content.offsetWidth;
      let buttonWidth = PREV_NEXT_CONTAINER_WIDTH * 2;
      if (contentWidth - distanceToEnd >= PREV_NEXT_CONTAINER_WIDTH) {
        buttonWidth -= PREV_NEXT_CONTAINER_WIDTH;
      }
      const width = contentWidth - buttonWidth;
      const pos = content.scrollLeft + width;
      const lastPos = content.scrollWidth - width;
      content.scrollLeft = pos >= lastPos ? lastPos : pos;
    }
  }
};

export { script as default };
//# sourceMappingURL=pendo-tabs.vue2.js.map
